/**
 * Where a document stands on MONEY — a different question from its workflow
 * `status`, which is what the document lists showed until now. An invoice can
 * sit at "issued" for a month with half of it collected, and the only thing the
 * list said was "Issued".
 *
 * Kept out of the page component so the rule can be tested on its own: the
 * edge cases here (a refund, a draft, the reader's midnight) are exactly the
 * ones that would otherwise be discovered on a customer's statement.
 */

/** The subset of a document row this rule reads. */
export interface PayableRow {
  status: string;
  total?: string;
  amount?: string;
  /** Present only on document types that carry a settlement figure. */
  amountPaid?: string;
  dueDate?: string | null;
  isCreditNote?: number | boolean;
  isDebitNote?: number | boolean;
}

/**
 * 'none' = the question does not apply — a draft nobody owes yet, a cancelled
 * document, a type with no paid figure at all, or a credit/debit note, which is
 * money owed BACK and would read as a debt if it were shown as unpaid.
 */
export type PayState = 'paid' | 'partial' | 'unpaid' | 'overdue' | 'none';

const numberOf = (v: unknown): number => Number(v ?? 0) || 0;

/**
 * A document that no longer counts: cancelled (invoices, bills, orders) or
 * voided (payments). Its journal entry is reversed, so it is in no balance —
 * a list must not add it to a total or show its amount as owed.
 */
export function isVoidDoc(row: { status: string }): boolean {
  return row.status === 'cancelled' || row.status === 'voided' || row.status === 'void';
}

/** Still owed on this document. Never negative — an overpayment is not a debt; a cancelled one owes nothing. */
export function remainingOf(row: PayableRow): number {
  if (isVoidDoc(row)) return 0;
  return Math.max(0, numberOf(row.total ?? row.amount) - numberOf(row.amountPaid));
}

export function payStateOf(row: PayableRow, today: string): PayState {
  if (row.amountPaid == null) return 'none';
  if (row.isCreditNote || row.isDebitNote) return 'none';
  if (row.status === 'cancelled' || row.status === 'draft') return 'none';
  if (remainingOf(row) <= 0) return 'paid';
  const due = (row.dueDate ?? '').slice(0, 10);
  // Overdue outranks partly-paid deliberately: half-collected and a week late is
  // a chase, not a progress bar. The Paid/Remaining columns still show the split.
  if (due && due < today) return 'overdue';
  return numberOf(row.amountPaid) > 0 ? 'partial' : 'unpaid';
}

/**
 * Today in the READER's timezone.
 *
 * `new Date().toISOString()` is UTC: at 01:00 in Iraq it still says yesterday,
 * so every invoice due today would be branded overdue for the first three hours
 * of every working day.
 */
export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
